import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRepairOrder, useRepairOrders } from '../../hooks/useRepairOrders';
import { repairOrdersService } from '../../services/repairOrdersService';
import { formatCurrency } from '../../utils/currency';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ShareButton from '../../components/ui/ShareButton';

const RepairOrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { order, loading, error } = useRepairOrder(id || null);
  const { deleteRepairOrder, deleting } = useRepairOrders();
  const { businessId } = useAuth();

  const handleDelete = async () => {
    if (!order) return;

    // Check if order can be deleted (only pending or cancelled)
    if (order.status !== 'pending' && order.status !== 'cancelled') {
      alert('Only pending or cancelled repair orders can be deleted');
      return;
    }

    if (window.confirm('Are you sure you want to delete this repair order? This action cannot be undone.')) {
      try {
        const { error } = await deleteRepairOrder(order.id);

        if (error) {
          alert(`Failed to delete repair order: ${error.message}`);
        } else {
          alert('Repair order deleted successfully');
          navigate('/repair-orders');
        }
      } catch (err) {
        alert('Failed to delete repair order');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {error || 'Repair order not found'}
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Repair Orders', href: '/repair-orders' },
    { label: order.repair_order_number, current: true }
  ];

  // Calculate total parts cost
  const totalPartsCost = order.items.reduce((sum, item) => sum + item.total_price, 0);

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Repair Order {order.repair_order_number}
          </h1>
          <p className="text-gray-600">
            Created on {new Date(order.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex space-x-2">
           <ShareButton
             repairOrderId={order.id}
             repairOrderNumber={order.repair_order_number}
             customerPhone={order.customer?.phone}
             customerEmail={order.customer?.email}
           />
           {(order.status === 'pending' || order.status === 'process') && (
             <Button
               variant="outline"
               onClick={() => navigate(`/repair-orders/${order.id}/edit`)}
             >
               Edit
             </Button>
           )}
           {(order.status === 'pending' || order.status === 'cancelled') && (
             <Button
               variant="primary"
               onClick={handleDelete}
               disabled={deleting === order.id}
               className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 disabled:opacity-50"
             >
               {deleting === order.id ? 'Deleting...' : 'Delete'}
             </Button>
           )}
         </div>
      </div>

      <div className="space-y-6">
        {/* Repair Order Details Container */}
        <div className="card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Repair Order Details</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {/* First Row - 3 columns */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-6">
              <div>
                <dt className="text-sm font-medium text-gray-500">Repair Order Number</dt>
                <dd className="mt-1 text-sm text-gray-900 font-medium">{order.repair_order_number}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Entry Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(order.entry_date).toLocaleDateString()}
                </dd>
              </div>
              {order.completed_date && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Completed Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(order.completed_date).toLocaleDateString()}
                  </dd>
                </div>
              )}
            </div>

            {/* Second Row - 3 columns */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Customer</dt>
                <dd className="mt-1 text-sm">
                  {order.customer ? (
                    <button
                      onClick={() => navigate(`/contacts/${order.customer!.id}`)}
                      className="text-blue-600 hover:text-blue-800 underline hover:no-underline cursor-pointer"
                    >
                      {order.customer.name}
                    </button>
                  ) : (
                    <span className="text-gray-900">Unknown Customer</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Technician</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {order.technician?.name || 'Unassigned'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className={`badge ${repairOrdersService.getStatusBadgeClass(order.status)}`}>
                    {repairOrdersService.getStatusLabel(order.status)}
                  </span>
                </dd>
              </div>
            </div>
          </div>
        </div>

        {/* Device Information Container */}
        <div className="card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Device Information</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {/* First Row - 3 columns */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-6">
              <div>
                <dt className="text-sm font-medium text-gray-700">Device Brand</dt>
                <dd className="mt-1 text-sm text-gray-900">{order.device_brand}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-700">Device Model</dt>
                <dd className="mt-1 text-sm text-gray-900">{order.device_model}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-700">Device IMEI</dt>
                <dd className="mt-1 text-sm text-gray-900">{order.device_serial}</dd>
              </div>
            </div>

            {/* Second Row - 2 columns */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-700">Problem</dt>
                <dd className="mt-1 text-sm text-gray-900">{order.device_problem}</dd>
              </div>
              {order.device_diagnosis && (
                <div>
                  <dt className="text-sm font-medium text-gray-700">Diagnosis</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.device_diagnosis}</dd>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Spare Part Items Container */}
        <div className="card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Spare Part Items</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {order.items.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No parts used in this repair.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Part
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {order.items.map((item) => (
                      <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.item_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCurrency(item.unit_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(item.total_price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={3} className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                        Total Parts Cost
                      </td>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">
                        {formatCurrency(totalPartsCost)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default RepairOrderDetails;